// Fil des gestes (DS §6.16, Task B2) : tampon circulaire des 30 derniers
// évènements de l'écran (navigation, clics, soumissions, appels API, erreurs),
// joint à un signalement quand quelqu'un en envoie un. Composant sentinelle
// monté UNE FOIS dans App.tsx, sous les providers — il ne rend rien (`null`)
// et ne casse JAMAIS une page : chaque écouteur est enrobé de son propre
// try/catch, l'échec d'un geste isolé ne doit pas faire planter l'app qui le
// consigne.
//
// `fil` est un singleton MODULE (pas un contexte React) : SignalerProbleme
// l'importe directement pour lire les 30 derniers gestes au moment de l'envoi,
// sans avoir à percer un contexte jusqu'à lui.

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { describeClickTarget, describeSubmit } from "../lib/signalement/describe-target";
import { apiEventFromFetch, createFil, truncateError } from "../lib/signalement/fil";

declare global {
  interface Window {
    /** Garde-fou : le pont fetch n'est posé qu'une seule fois par session. */
    __filFetchWrapped?: boolean;
  }
}

function sessionStorageOuNull(): Pick<Storage, "getItem" | "setItem"> | null {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch {
    // Mode privé, quota, ou storage bloqué par une politique du navigateur.
    return null;
  }
}

export const fil = createFil(sessionStorageOuNull(), () => new Date().toISOString());

function pousserEvenementApi(
  evenement: { methode: string; chemin: string } | null,
  statut: number | "reseau",
  debut: number,
): void {
  if (!evenement) return;
  try {
    fil.push({
      kind: "api",
      methode: evenement.methode,
      chemin: evenement.chemin,
      statut,
      dureeMs: Date.now() - debut,
    });
  } catch {
    // Le fil ne doit jamais casser un appel réseau applicatif.
  }
}

// `window.fetch` n'est enrobé qu'UNE fois par session (le composant peut être
// démonté/remonté, ex. Fast Refresh) — la garde vit sur `window`, pas dans une
// réf locale. Fonction à part : le corps async imbriqué faisait grimper la
// complexité cognitive de l'effet appelant au-delà du seuil autorisé.
function envelopperFetch(
  fetchOriginal: typeof window.fetch,
  supabaseUrl: string,
): typeof window.fetch {
  return (async (input: RequestInfo | URL, init?: RequestInit) => {
    const debut = Date.now();
    const evenement = apiEventFromFetch(input, init, supabaseUrl);
    try {
      const reponse = await fetchOriginal(input, init);
      pousserEvenementApi(evenement, reponse.status, debut);
      return reponse;
    } catch (err) {
      pousserEvenementApi(evenement, "reseau", debut);
      throw err;
    }
  }) as typeof window.fetch;
}

export function FilAriane(): null {
  const location = useLocation();

  // Navigation : un évènement par changement de route (y compris le montage).
  useEffect(() => {
    try {
      fil.push({ kind: "page", path: location.pathname });
    } catch {
      // Le fil ne doit jamais casser une page.
    }
  }, [location.pathname]);

  // Écouteurs globaux : montés une seule fois pour la durée de vie du composant.
  useEffect(() => {
    function onClickCapture(e: MouseEvent) {
      try {
        if (!(e.target instanceof Element)) return;
        const cible = describeClickTarget(e.target);
        if (!cible) return;
        fil.push({ kind: "clic", cible });
      } catch {
        // idem : un clic manqué au fil n'est jamais une erreur pour l'écran.
      }
    }

    function onSubmit(e: Event) {
      try {
        if (!(e.target instanceof HTMLFormElement)) return;
        const { formulaire, champs } = describeSubmit(e.target);
        fil.push({ kind: "soumission", formulaire, champs });
      } catch {
        // idem.
      }
    }

    function onError(e: ErrorEvent) {
      try {
        fil.push({ kind: "erreur", message: truncateError(e.error ?? e.message) });
      } catch {
        // idem.
      }
    }

    function onRejection(e: PromiseRejectionEvent) {
      try {
        fil.push({ kind: "erreur", message: truncateError(e.reason) });
      } catch {
        // idem.
      }
    }

    // Phase capture : on veut voir le clic avant qu'un `stopPropagation` d'un
    // gestionnaire applicatif ne l'arrête en phase bouillonnante.
    document.addEventListener("click", onClickCapture, true);
    document.addEventListener("submit", onSubmit, true);
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    // console.error enrobé : les erreurs journalisées (§6.13 : le motif
    // technique part au journal) entrent aussi dans le fil, sans rien changer
    // au journal lui-même.
    const consoleErreurOriginal = console.error;
    console.error = (...args: unknown[]) => {
      try {
        fil.push({ kind: "erreur", message: truncateError(args.map(String).join(" ")) });
      } catch {
        // idem.
      }
      consoleErreurOriginal(...args);
    };

    if (!window.__filFetchWrapped) {
      window.__filFetchWrapped = true;
      const fetchOriginal = window.fetch.bind(window);
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL ?? "";
      window.fetch = envelopperFetch(fetchOriginal, supabaseUrl);
    }

    return () => {
      document.removeEventListener("click", onClickCapture, true);
      document.removeEventListener("submit", onSubmit, true);
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      console.error = consoleErreurOriginal;
      // Le pont fetch n'est PAS retiré : FilAriane vit pour la durée de l'app,
      // et la garde `__filFetchWrapped` empêche un double enrobage si jamais
      // le composant est remonté.
    };
  }, []);

  return null;
}
